import { gql, useLazyQuery } from '@apollo/client';
import * as React from 'react';

import Button from '../../components/Button';
import Header from '../../components/Header';
import Input from '../../components/Input';
import Table from '../../components/Table';

import type {
  AdminInspectObjectQuery,
  AdminInspectObjectQueryVariables,
} from './__generated__/ObjectInspectorScene.gql';

const ADMIN_INSPECT_OBJECT = gql`
  query AdminInspectObject($id: String!) {
    adminInspectObject(id: $id) {
      primary {
        type
        id
        data
      }
      related {
        type
        count
        objects {
          type
          id
          data
        }
      }
    }
  }
`;

type InspectorResult = NonNullable<AdminInspectObjectQuery['adminInspectObject']>;
type InspectedObject = InspectorResult['primary'];
type RelatedGroup = InspectorResult['related'][number];

/**
 * Object Inspector admin tool. Operator pastes a UUID; the resolver searches
 * every inspectable table for a matching row, returns it as the "primary",
 * and walks tenancy anchors (`user_id` / `family_id`) to bucket every
 * related row across other tables. The UI renders the primary as a key/value
 * table at the top and each related group as a collapsible section below.
 *
 * Designed for "I have an id from a log line / Sentry breadcrumb / DB query
 * — what is it?" debugging. Returns an empty state for unknown ids and
 * surfaces redacted values (`[REDACTED]`) verbatim so operators see exactly
 * what the resolver suppressed.
 */
const ObjectInspectorScene = () => {
  const [inputId, setInputId] = React.useState('');
  const [submittedId, setSubmittedId] = React.useState<string | null>(null);
  // Related groups start collapsed by default — operators usually scan group
  // counts first and only expand the group they care about. Tracking the
  // expanded set (rather than the collapsed set) makes the default trivial
  // and survives result refreshes without per-fetch reset logic.
  const [expandedGroups, setExpandedGroups] = React.useState<Set<string>>(new Set());

  const [inspect, { data, loading, error }] = useLazyQuery<AdminInspectObjectQuery, AdminInspectObjectQueryVariables>(
    ADMIN_INSPECT_OBJECT,
    { fetchPolicy: 'network-only' }
  );

  const handleInspect = () => {
    const trimmed = inputId.trim();
    if (trimmed.length === 0) {
      return;
    }
    setSubmittedId(trimmed);
    setExpandedGroups(new Set());
    void inspect({ variables: { id: trimmed } });
  };

  const toggleGroup = (type: string) => {
    setExpandedGroups((prev) => {
      const next = new Set(prev);
      if (next.has(type)) {
        next.delete(type);
      } else {
        next.add(type);
      }
      return next;
    });
  };

  const result = data?.adminInspectObject ?? null;

  return (
    <div className="bg-bg-primary text-fg-primary flex min-h-screen flex-col">
      <Header />
      <main className="flex flex-1 flex-col gap-4 px-6 py-5">
        <h1 className="text-sm font-semibold uppercase tracking-widest">Object inspector</h1>

        {/* Lookup row */}
        <div className="border-border-primary flex items-end gap-2 border-b pb-4">
          <Input
            className="w-[28rem]"
            label="Object id"
            placeholder="UUID, Plaid item / account id, message id…"
            value={inputId}
            onChange={setInputId}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                handleInspect();
              }
            }}
            hint="Searches both internal UUIDs and external ids (Plaid `item_…`, `acc_…`, etc.)."
          />
          <Button
            color="primary"
            size="sm"
            onClick={handleInspect}
            isLoading={loading}
            isDisabled={inputId.trim().length === 0}
          >
            Inspect
          </Button>
        </div>

        {/* Result */}
        {error && (
          <div className="text-fg-danger-primary text-xs">Failed to inspect object. Check the id and try again.</div>
        )}

        {!loading && !error && submittedId !== null && result === null && (
          <div className="text-fg-tertiary text-xs italic">
            No row found with id <span className="font-mono">{submittedId}</span> in any inspectable table.
          </div>
        )}

        {result !== null && (
          <div className="flex flex-col gap-6">
            <PrimarySection primary={result.primary} />
            <RelatedSection groups={result.related} expandedGroups={expandedGroups} onToggleGroup={toggleGroup} />
          </div>
        )}
      </main>
    </div>
  );
};

interface IPrimarySectionProps {
  primary: InspectedObject;
}

const PrimarySection = ({ primary }: IPrimarySectionProps) => (
  <section className="flex flex-col gap-2">
    <h2 className="text-fg-tertiary text-xs font-medium uppercase tracking-wider">
      Primary · <span className="text-fg-brand-primary">{primary.type}</span>
    </h2>
    <ObjectTable object={primary} />
  </section>
);

interface IRelatedSectionProps {
  groups: RelatedGroup[];
  expandedGroups: Set<string>;
  onToggleGroup: (type: string) => void;
}

const RelatedSection = ({ groups, expandedGroups, onToggleGroup }: IRelatedSectionProps) => {
  if (groups.length === 0) {
    return <div className="text-fg-tertiary text-xs italic">No related rows for this object.</div>;
  }
  return (
    <section className="flex flex-col gap-3">
      <h2 className="text-fg-tertiary text-xs font-medium uppercase tracking-wider">
        Related · {groups.length} {groups.length === 1 ? 'group' : 'groups'}
      </h2>
      <div className="flex flex-col gap-2">
        {groups.map((group) => (
          <RelatedGroupSection
            key={group.type}
            group={group}
            isExpanded={expandedGroups.has(group.type)}
            onToggle={() => onToggleGroup(group.type)}
          />
        ))}
      </div>
    </section>
  );
};

interface IRelatedGroupSectionProps {
  group: RelatedGroup;
  isExpanded: boolean;
  onToggle: () => void;
}

const RelatedGroupSection = ({ group, isExpanded, onToggle }: IRelatedGroupSectionProps) => {
  // `group.count` is the total matching-row count; `group.objects.length` is
  // capped at the per-table fetch limit on the server. Surface "X of Y" when
  // they diverge so operators know more rows exist beyond the rendered list.
  const isTruncated = group.objects.length < group.count;
  const countLabel = isTruncated
    ? `showing ${group.objects.length} of ${group.count}`
    : `${group.count} ${group.count === 1 ? 'row' : 'rows'}`;
  return (
    <div className="border-border-primary border">
      <button
        type="button"
        onClick={onToggle}
        className="hover:bg-bg-secondary flex w-full items-center justify-between px-3 py-2 text-left"
      >
        <span className="flex items-baseline gap-2">
          <span className="text-fg-primary text-sm font-medium">{group.type}</span>
          <span className="text-fg-tertiary font-mono text-xs">{countLabel}</span>
        </span>
        <span className="text-fg-tertiary font-mono text-xs">{isExpanded ? '−' : '+'}</span>
      </button>
      {isExpanded && (
        <div className="border-border-primary flex flex-col gap-3 border-t px-3 py-3">
          {group.objects.map((object) => (
            <ObjectTable key={object.id} object={object} />
          ))}
        </div>
      )}
    </div>
  );
};

interface IObjectTableProps {
  object: InspectedObject;
}

const ObjectTable = ({ object }: IObjectTableProps) => {
  const entries = Object.entries(object.data).sort(([a], [b]) => a.localeCompare(b));
  return (
    <div className="border-border-primary border">
      <div className="border-border-primary flex items-baseline gap-2 border-b px-3 py-2">
        <span className="text-fg-tertiary text-xs uppercase tracking-wider">{object.type}</span>
        <span className="text-fg-secondary font-mono text-xs">{object.id}</span>
      </div>
      <Table>
        <Table.Body>
          {entries.map(([key, value]) => (
            <Table.Row key={key}>
              <Table.Cell muted>{key}</Table.Cell>
              <Table.Cell mono>{formatValue(value)}</Table.Cell>
            </Table.Row>
          ))}
        </Table.Body>
      </Table>
    </div>
  );
};

/**
 * Renders a single column value for the row table. The inspector returns
 * raw row data through the `Object` JSON scalar, so values can be strings,
 * numbers, booleans, null, or nested JSON. We stringify nested objects
 * inline rather than rendering a tree — keeps the UI compact and the
 * payload diffable when the operator is comparing two rows side-by-side.
 */
function formatValue(value: unknown): string {
  if (value === null || value === undefined) {
    return '—';
  }
  if (typeof value === 'string') {
    return value;
  }
  if (typeof value === 'number' || typeof value === 'boolean') {
    return String(value);
  }
  return JSON.stringify(value);
}

export default ObjectInspectorScene;
